/* eslint-disable jira/filename-case */
export const WebStorageType = {
	LocalStorage: 'LocalStorage',
	SessionStorage: 'SessionStorage',
} as const;

export type WebStorageProvider = {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	get: (key: string) => any;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	set: (key: string, value?: any) => void;
	remove: (key: string) => void;
	clearAll: () => void;
};

export type WebStorageProviderNew = {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	set: (key: string, value: any) => void;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	setAsync: (key: string, value: any) => Promise<void>;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	setStrictlyNecessaryItem: (key: string, value: any) => void;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	get: (key: string) => any | undefined;
	remove: (key: string) => void;
	clearAll: () => void;
	length: () => number | undefined;
	key: (index: number) => string | null;
};

export type ExpirableWebStorageProvider = {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	get: (key: string) => any;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	set: (key: string, value?: any, expiration?: number) => any;
	remove: (key: string) => void;
	removeExpiredKeys: () => void;
};

export type ExpirableWebStorageProviderNew = {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	set: (key: string, value: any, expiration?: number) => void;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	setAsync: (key: string, value: any, expiration?: number) => Promise<void>;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	setStrictlyNecessaryItem: (key: string, value: any, expiration?: number) => void;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	get: (key: string) => any | undefined;
	remove: (key: string) => void;
	removeExpiredKeys: () => void;
	clearAll: () => void;
	getExpiration: (key: string) => number | undefined;
	length: () => number | undefined;
	key: (index: number) => string | null;
};

export type AsyncStorage = {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	get: <T = any>(key: string) => Promise<T | undefined>;
	/**
	 * Transforming a value (eg incrementing a number) using `get` and `set` is
	 * risky, as both `get` and `set` are async and non-atomic. If the value you
	 * are setting depends on the current value stored in the IndexedDB, you can
	 * pass an `updater` function as the second argument which will receive the
	 * current value as an argument and expects the new value to be returned.
	 *
	 * @example
	 *
	 * // Don't do this:
	 * import { get, set } from 'idb-keyval';
	 *
	 * get('counter').then((val) =>
	 *      set('counter', (val || 0) + 1)
	 *  );
	 *
	 * get('counter').then((val) =>
	 *      set('counter', (val || 0) + 1) // Counter will be 1 here instead of 2
	 *  );
	 *
	 * // Do this instead
	 * import { set } from 'idb-keyval';
	 *
	 * set('counter', (val) => (val || 0) + 1);
	 * set('counter', (val) => (val || 0) + 1); // Counter will be 2 here
	 *
	 */
	set: <T>(key: string, value?: T | ((oldValue: T | undefined) => T)) => Promise<void>;
	remove: (key: string) => Promise<void>;
	clearAll: () => Promise<void>;
	reconnect: () => Promise<void>;
};

export type SynchronousStorage =
	| WebStorageProvider
	| WebStorageProviderNew
	| ExpirableWebStorageProvider
	| ExpirableWebStorageProviderNew;

export type Status = 'SUCCESS' | 'BLOCKED' | 'FAILED' | 'ERROR';

export type SetItemResult =
	| {
			status: Exclude<Status, 'ERROR'>;
	  }
	| {
			status: 'ERROR';
			error: Error;
	  };

export type AtlBrowserStorageProvider = {
	getItem: (key: string) => string | null;
	setItem: (key: string, value: string) => Promise<SetItemResult>;
	setStrictlyNecessaryItem: (key: string, value: string) => void;
	removeItem: (key: string) => void;
	key: (index: number) => string | null;
	length: () => number;
	clear: () => void;
};

export type BrowserStorageProvider = SynchronousStorage | AtlBrowserStorageProvider;
